import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { LOGO_URL, waLink } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/nri-corner", label: "NRI Corner" },
  { to: "/properties", label: "Properties" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "bg-white/95 backdrop-blur shadow-[0_1px_0_0_#00A7D8]" : "bg-white"}`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <img src={LOGO_URL} alt="Hi-Tech Realty" className="h-12 w-12 object-contain" />
          <span className="hidden sm:block leading-tight">
            <span className="block font-display text-xl font-bold text-brand-navy">Hi-Tech Realty</span>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-brand-cyan font-semibold">Chennai Property Care</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`header-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors ${isActive ? "text-brand-cyan" : "text-brand-navy hover:text-brand-cyan"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.role === "owner" && (
            <Link to="/owner/dashboard" data-testid="header-owner-dashboard" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-navy hover:text-brand-cyan">My Dashboard</Link>
          )}
          {user && user.role === "tenant" && (
            <Link to="/tenant/dashboard" data-testid="header-tenant-dashboard" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-navy hover:text-brand-cyan">Tenant Portal</Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="header-admin" className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-cyan hover:underline">Admin Console</Link>
          )}
          {!user && (
            <>
              <Link to="/login/owner" data-testid="header-owner-login" className="btn-ghost-navy !py-2 !px-4">Owner Login</Link>
              <Link to="/login/tenant" data-testid="header-tenant-login" className="btn-ghost-navy !py-2 !px-4">Tenant Login</Link>
            </>
          )}
          <a
            data-testid="header-whatsapp"
            href={waLink("Hi Hi-Tech Realty, I want to know more about Property Management & Monitoring services in Chennai.")}
            target="_blank"
            rel="noreferrer"
            className="btn-whatsapp !py-2 !px-4"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-brand-navy"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-border bg-white" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-base font-semibold ${isActive ? "text-brand-cyan" : "text-brand-navy"}`}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-3 pt-4 border-t border-brand-border">
              <Link to="/login/owner" data-testid="mobile-owner-login" className="btn-ghost-navy flex-1">Owner Login</Link>
              <Link to="/login/tenant" data-testid="mobile-tenant-login" className="btn-ghost-navy flex-1">Tenant Login</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
